import React from "react"

import { ResponsiveBar } from "@nivo/bar"
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { DateFilter } from "@/types/filters"

import { useAssessmentFalloffData } from "../../../hooks/useAssessmentFalloffData"
import { useOrientationAttendanceData } from "../../../hooks/useOrientationAttendanceData"
import { useStageProgressionData } from "../../../hooks/useStageProgressionData"
import { useTrialResultsData } from "../../../hooks/useTrialResultsData"

interface ConversionTabProps {
  dateFilter?: DateFilter
  jobId?: string
}

// Update the hook return type to match the new API response
type StageProgressionData = {
  stageCounts: Record<string, number>
}

const stageColors = {
  sjt: "#3B82F6",
  neurapulse: "#3B82F6",
  "driver-onroad-test": "#3B82F6",
  documents: "#10B981",
  orientation: "#F97316",
  "orientation-quiz": "#F97316",
  "personal-details": "#10B981",
  trial: "#F97316",
  contract: "#10B981",
  "vehicle-collection": "#FFBE37",
  complete: "#22C55E",
  rejected: "#EF4444",
}

const ConversionTab: React.FC<ConversionTabProps> = ({ dateFilter, jobId }) => {
  // Fetch stage progression data
  const {
    data: stageProgressionData,
    isLoading: isLoadingStageData,
    error: stageDataError,
  } = useStageProgressionData(dateFilter, jobId) as {
    data: StageProgressionData | undefined
    isLoading: boolean
    error: Error | null
  }

  // Fetch driver-onroad-test assessment falloff data
  const {
    data: driverTestFalloffData,
    isLoading: isDriverTestDataLoading,
    error: driverTestDataError,
  } = useAssessmentFalloffData("driver-onroad-test", dateFilter, jobId)

  // Fetch orientation attendance data
  const {
    data: orientationAttendanceData,
    isLoading: isOrientationDataLoading,
    error: orientationDataError,
  } = useOrientationAttendanceData(dateFilter, jobId)

  // Fetch trial results data
  const {
    data: trialResultsData,
    isLoading: isTrialResultsLoading,
    error: trialResultsError,
  } = useTrialResultsData(dateFilter, jobId)

  // Custom tooltip to show both absolute values and percentages for assessment charts
  const AssessmentTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean
    payload?: Array<{ value: number; name: string }>
    label?: string
  }) => {
    if (active && payload && payload.length) {
      const completed = payload[0].value
      const dropped = payload[1].value
      const total = completed + dropped
      const completedPercentage = total > 0 ? ((completed / total) * 100).toFixed(1) : "0"
      const droppedPercentage = total > 0 ? ((dropped / total) * 100).toFixed(1) : "0"

      return (
        <div className="bg-white p-2 shadow rounded border border-palette-jungleGreen200">
          <p className="font-semibold">Question {label}</p>
          <p className="text-sm text-green-600">
            Completed: {completed} ({completedPercentage}%)
          </p>
          <p className="text-sm text-red-600">
            Dropped: {dropped} ({droppedPercentage}%)
          </p>
          <p className="text-sm font-medium">Total: {total}</p>
        </div>
      )
    }
    return null
  }

  // Transform stage counts data for the bar chart with conversion percentages
  const transformStageCountsToBarData = () => {
    if (!stageProgressionData || !stageProgressionData.stageCounts) return []

    // Get the ordered stages from the API
    const orderedStages = Object.keys(stageProgressionData.stageCounts)

    // Create the bar data with conversion percentages
    return orderedStages.map((stage, index) => {
      const count = stageProgressionData.stageCounts[stage]
      let conversionRate = null

      // Calculate conversion rate compared to previous stage (if not the first stage)
      if (index > 0) {
        const previousStage = orderedStages[index - 1]
        const previousCount = stageProgressionData.stageCounts[previousStage]

        if (previousCount > 0) {
          conversionRate = ((count / previousCount) * 100).toFixed(0)
        }
      }

      return {
        stage: stage.replace(/-/g, " "),
        count,
        conversionRate,
        color: stageColors[stage] || "#3B82F6",
      }
    })
  }
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Stage Counts Bar Chart */}
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle>Applications by Stage</CardTitle>
            <CardDescription>Number of applications that reached each stage</CardDescription>
          </CardHeader>
          <CardContent className="pt-2 h-80">
            {isLoadingStageData ? (
              <div className="flex justify-center items-center h-full">
                <Skeleton className="h-full w-full" />
              </div>
            ) : stageDataError ? (
              <div className="flex justify-center items-center h-full">
                <p className="text-red-500">Error loading stage data</p>
              </div>
            ) : stageProgressionData &&
              stageProgressionData.stageCounts &&
              Object.values(stageProgressionData.stageCounts).some((count) => count > 0) ? (
              <ResponsiveBar
                data={transformStageCountsToBarData()}
                keys={["count"]}
                indexBy="stage"
                margin={{ top: 70, right: 30, bottom: 100, left: 60 }}
                padding={0.2}
                layout="vertical"
                valueScale={{ type: "linear" }}
                enableTotals={true}
                gridYValues={(() => {
                  const data = transformStageCountsToBarData()
                  if (data.length === 0) return [0, 1]
                  const maxCount = Math.max(...data.map((d) => d.count))
                  return Array.from({ length: Math.ceil(maxCount) + 1 }, (_, i) => i)
                })()}
                indexScale={{ type: "band", round: true }}
                colors={({ data }) => data.color}
                borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: "Stage",
                  legendPosition: "middle",
                  legendOffset: 80,
                }}
                axisLeft={{
                  tickSize: 0,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: "Applications",
                  legendPosition: "middle",
                  legendOffset: -50,
                  format: (value) => (Math.floor(value) === value ? value : ""),
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                animate={true}
                layers={[
                  "grid",
                  "axes",
                  "bars",
                  "markers",
                  "legends",
                  "annotations",
                  // Custom layer to add conversion rate percentages above bars
                  (props) => {
                    const { bars } = props

                    return (
                      <g>
                        {bars.map((bar) => {
                          // In Nivo, the original data is stored in bar.data.data
                          const originalData = bar.data.data
                          const conversionRate = originalData ? originalData.conversionRate : null

                          // Only show conversion rate if it exists (not for first bar)
                          if (!conversionRate) return null

                          return (
                            <g key={`conversion-${bar.key}`}>
                              {/* Simple text with no background */}
                              <text
                                x={bar.x + bar.width / 2}
                                y={bar.y - 20}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "normal",
                                  fill: "#000000",
                                }}
                              >
                                {conversionRate}%
                              </text>
                            </g>
                          )
                        })}
                      </g>
                    )
                  },
                ]}
                tooltip={({ data, value }) => (
                  <div className="bg-white p-2 shadow rounded border border-palette-jungleGreen200">
                    <strong className="capitalize">{data.stage}</strong>
                    <p className="text-sm whitespace-nowrap">Applications: {value}</p>
                    {data.conversionRate && (
                      <p className="text-sm whitespace-nowrap">
                        Conversion: {data.conversionRate}%
                      </p>
                    )}
                  </div>
                )}
              />
            ) : (
              <div className="flex justify-center items-center h-full">
                <p className="text-palette-jungleGreen500">No stage data available</p>
              </div>
            )}
          </CardContent>
        </Card>
        {/* Driver On-Road Test Assessment Falloff Card */}
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle>Driver On-Road Test Falloff</CardTitle>
            <CardDescription>Completion and dropout rates by question number</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            {isDriverTestDataLoading ? (
              <div className="flex justify-center items-center h-full">
                <Skeleton className="h-full w-full" />
              </div>
            ) : driverTestDataError ? (
              <div className="flex justify-center items-center h-full">
                <p className="text-red-500">Error loading driver test falloff data</p>
              </div>
            ) : driverTestFalloffData && driverTestFalloffData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={driverTestFalloffData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="question"
                    label={{
                      value: "Question Number",
                      position: "insideBottom",
                      offset: -5,
                    }}
                  />
                  <YAxis
                    label={{
                      value: "Count",
                      angle: -90,
                      position: "insideLeft",
                    }}
                    allowDecimals={false}
                    domain={[0, "dataMax + 1"]}
                  />
                  <Tooltip content={<AssessmentTooltip />} />
                  <Bar dataKey="completed" stackId="a" fill="#10B981" name="Completed" />
                  <Bar dataKey="dropped" stackId="a" fill="#EF4444" name="Dropped" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex justify-center items-center h-full">
                <p className="text-palette-jungleGreen500">No driver test data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Orientation Attendance Rate Card */}
        <Card>
          <CardHeader>
            <CardTitle>Orientation Attendance Rate</CardTitle>
            <CardDescription>Attendance statistics for orientation sessions</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {isOrientationDataLoading ? (
              <div className="flex justify-center items-center h-full">
                <Skeleton className="h-[150px] w-full" />
              </div>
            ) : orientationDataError ? (
              <div className="flex justify-center items-center h-full">
                <p className="text-red-500">Error loading orientation attendance data</p>
              </div>
            ) : orientationAttendanceData ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="text-2xl font-bold">
                    {orientationAttendanceData.attendanceRate.toFixed(1)}%
                  </div>
                  <div className="text-sm text-muted-foreground">Attendance Rate</div>
                </div>
                <div className="h-4 w-full bg-palette-jungleGreen200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full"
                    style={{
                      width: `${orientationAttendanceData.attendanceRate}%`,
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground">Attended</div>
                    <div className="text-2xl font-bold text-green-600">
                      {orientationAttendanceData.attendedCount}
                    </div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground">No-Shows</div>
                    <div className="text-2xl font-bold text-red-600">
                      {orientationAttendanceData.noShowCount}
                    </div>
                  </div>
                </div>
                <div className="pt-2 text-sm text-muted-foreground">
                  Total Bookings: {orientationAttendanceData.totalBookings}
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center h-full">
                <p className="text-palette-jungleGreen500">
                  No orientation attendance data available
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trial Results Card */}
        <Card>
          <CardHeader>
            <CardTitle>Trial Pass/Fail Rate</CardTitle>
            <CardDescription>Pass and fail statistics for driver trials</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {isTrialResultsLoading ? (
              <div className="flex justify-center items-center h-full">
                <Skeleton className="h-[150px] w-full" />
              </div>
            ) : trialResultsError ? (
              <div className="flex justify-center items-center h-full">
                <p className="text-red-500">Error loading trial results data</p>
              </div>
            ) : trialResultsData ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="text-2xl font-bold">{trialResultsData.passRate.toFixed(1)}%</div>
                  <div className="text-sm text-muted-foreground">Pass Rate</div>
                </div>
                <div className="h-4 w-full bg-palette-jungleGreen200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${trialResultsData.passRate}%` }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground">Passed</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {trialResultsData.passCount}
                    </div>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground">Failed</div>
                    <div className="text-2xl font-bold text-amber-600">
                      {trialResultsData.failCount}
                    </div>
                  </div>
                </div>
                <div className="pt-2 text-sm text-muted-foreground">
                  Total Trials: {trialResultsData.totalTrials}
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center h-full">
                <p className="text-palette-jungleGreen500">No trial results data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ConversionTab
